/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { indexByFn } from 'volta/utils/array-utils';
import { deepEqual } from 'volta/utils/object-utils';

import type { IResourceName } from 'volta/models/base-model';
import type { IntlService } from 'ember-intl';
import type LabelService from 'volta/services/label-service';

interface BvFiltersFilterTagsSignature {
  Args: {
    disabled?: boolean;
    resourceName: IResourceName;
    filterDefinitions: TFilterDefinition[];
    filterValues: TFilterValues;
    onFilterValuesChange: (value: TFilterValue, field: string) => void;
  };
  Element: HTMLElement;
  Blocks: {
    default: [];
  };
}

export default class BvFiltersFilterTagsComponent extends Component<BvFiltersFilterTagsSignature> {
  @service labelService!: LabelService;
  @service intl!: IntlService;

  @tracked disabledSearchBtn: boolean = true;
  @tracked filterValues: TFilterValues = this.args.filterValues;

  @cached
  get filters(): (TFilterValue & { field: string })[] {
    return (Object.entries(this.args.filterValues) as [string, TFilterValue][]).map(
      ([field, f]: [string, TFilterValue]) => {
        return {
          ...f,
          field
        };
      }
    );
  }

  @cached
  get definitionsDict() {
    return indexByFn(this.args.filterDefinitions, (d) => d.queryParam);
  }

  @action
  onChange(filterValue: TFilterValue, field: string) {
    this.filterValues = { ...this.filterValues, [field]: filterValue };
    this.disabledSearchBtn = false;
  }

  @action
  handleInitialValuesChange(_element: HTMLElement, [initialFilterValues]: [TFilterValues]) {
    if (!deepEqual(this.filterValues, initialFilterValues)) {
      this.filterValues = initialFilterValues;
    }
  }

  @action
  handleSubmit(field: string) {
    if (this.filterValues[field]) {
      this.args.onFilterValuesChange(this.filterValues[field], field);
    }
    this.disabledSearchBtn = true;
  }

  getDef = (key: string): TFilterDefinition | undefined => this.definitionsDict[key];

  getFilter = (key?: string) => (key ? this.args.filterValues[key] : undefined);

  getTitle = (
    filter: TFilterValue & {
      field: string;
    },
    title?: string
  ) => {
    if (filter.isCustomField) {
      return this.labelService.tOrElse(filter.field, title);
    } else {
      return (
        title ??
        this.labelService.tOrElse(`filterDefinitions.${filter.field}`, this.intl.t(filter.field))
      );
    }
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvFilters::FilterTags': typeof BvFiltersFilterTagsComponent;
    'bv-filters/filter-tags': typeof BvFiltersFilterTagsComponent;
  }
}
